// eslint-disable-next-line @typescript-eslint/no-unused-vars
import styles from 'styles/giveaway-fonts.module.css'
import { IncomingMessage, ServerResponse } from 'http'
import Head from 'next/head'
import { slugs } from '@/constants/slugs'
import { ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { PresalesLayout } from '@/layouts/PresalesLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getProjectPhase } from '@/services/ProjectsService/ProjectsService'
import { getShopifyCollection, ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { getSweepstake } from '@/services/SweepstakeService'
import { PhaseSlug, SweepstakeObject } from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PreSalesGiveawayView } from '@/views/PreSales/PreSalesGiveawayView/PreSalesGiveawayView'

interface PreSalesPageProps {
  theatricalSlug: string
  tickets?: ShopifyProductWithBundles[]
  sweepstake: SweepstakeObject
  phase: PhaseSlug | null
  trailerExperimentVal: string | null
  experiments?: ServerExperiments
}

export const PreSalesPage: NextPageWithLayout<PreSalesPageProps> = ({
  tickets,
  theatricalSlug,
  phase,
  sweepstake,
  experiments,
}) => {
  return (
    <>
      <Head>
        <link
          rel="preload"
          href="https://images.angelstudios.com/raw/upload/v1750703334/angel-studios/fonts/woff2/compactacom-regular-webfont.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link rel="stylesheet" href="https://use.typekit.net/ljo6mjo.css" />
      </Head>
      <div className="font-compactaCom absolute inset-0 !top-0 !overflow-x-hidden bg-white lg:fixed">
        <PreSalesGiveawayView
          sweepstake={sweepstake}
          theatricalSlug={theatricalSlug}
          tickets={tickets}
          phase={phase}
          experiments={experiments}
        />
      </div>
    </>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ federationClient, locale, next404, params, res, req }) => {
    const theatricalSlug = params?.slug as string

    const sweepstake = await getSweepstake(federationClient, theatricalSlug)

    if (!sweepstake) {
      return next404()
    }

    const collectionId = sweepstake.bundlesCollectionId

    const tickets = collectionId ? await getShopifyCollection({ collectionId }) : null
    const phase = await getProjectPhase(federationClient, theatricalSlug)
    const experiments = await runGiveawayExperiments({ req, res, theatricalSlug })

    return {
      props: {
        tickets,
        theatricalSlug,
        phase,
        sweepstake,
        experiments: experiments ?? null,
        ...(await loadTranslations(locale, ['common', 'home', 'tickets', 'chatbot', 'account'])),
      },
    }
  },
)

async function runGiveawayExperiments({
  req,
  res,
  theatricalSlug,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  theatricalSlug: string
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = []

  if (theatricalSlug === slugs.sketch) {
    expConfigs.push({ experimentKey: 'freddie-wong-giveaway-cutout', defaultValue: false })
    expConfigs.push({ experimentKey: 'sketch-giveaway-partner-section', defaultValue: 'Default' })
  }
  if (theatricalSlug === slugs.youngWashington) {
    expConfigs.push({ experimentKey: 'young-washington-single-tab', defaultValue: false })
  }

  try {
    const experiments = await runExperimentsOnServer(req, res, expConfigs)
    return experiments || {}
  } catch (error) {
    logger().error('Failed to run experiments:', error)
    return expConfigs.reduce((acc, { experimentKey, defaultValue }) => {
      acc[experimentKey] = defaultValue
      return acc
    }, {} as ServerExperiments)
  }
}

PreSalesPage.getLayout = (page) => {
  return <PresalesLayout isDarkMode={false}>{page}</PresalesLayout>
}
export default PreSalesPage
